import {useWalletConnectionClient} from 'src/v2/state/walletConnection';
import {getStore} from 'src/v2/state/store';
import {syncWalletToSession} from 'src/v2/components/pay/syncWalletToSession';
import {useUpdateSessionOnWalletConnect} from 'src/v2/components/walletConnect/useUpdateSessionOnWalletConnect';
import {getAbiForAsset} from 'src/utils/getAbiForAsset';
import utf8ToHex from 'src/utils/utf8ToHex';
import type {SupportedCurrency} from 'src/v2/state/types';
import type {EvmTransactionData} from '@stripe-internal/wallet-connect-wrapper';
import type {OpenapiPostV1CryptoInternalRelayQuoteInputInputChainEnum} from '@stripe-internal/data-v1/schema/types';
import {useSessionContext} from 'src/state/usePayinSessionContext';
import {useIntl} from '@sail/intl';
import {handleError} from 'src/v2/components/error/handleError';
import {useResyncSessionState} from 'src/v2/components/pay/useResyncSessionState';

export const useHandleRelayPayment = () => {
  const intl = useIntl();
  const walletClient = useWalletConnectionClient();
  const store = getStore();
  const [updateSession] = useUpdateSessionOnWalletConnect();
  const {sessionId} = useSessionContext();
  const resyncSessionState = useResyncSessionState();

  const activeWallet = store.useSelector(
    (state) => state.walletConnection.activeWallet,
  );
  const walletSessions = store.useSelector((state) => state.walletSessions);

  const handleRelayPayment = async ({
    selectedCurrency,
    setIsProcessing,
  }: {
    selectedCurrency: SupportedCurrency;
    setIsProcessing: (processing: boolean) => void;
  }): Promise<void> => {
    if (!activeWallet) {
      return;
    }

    setIsProcessing(true);

    try {
      await syncWalletToSession(activeWallet, walletSessions, updateSession);

      const {quote} = store.getState().anyCryptoDeposit;
      if (!quote.depositAddress || !quote.inputAmount) {
        throw new Error('Relay quote not available');
      }

      const depositAddress = quote.depositAddress as `0x${string}`;
      const transferAmount = BigInt(
        Math.round(
          Number(quote.inputAmount) * 10 ** selectedCurrency.currencyMinorUnits,
        ),
      );

      const abi = getAbiForAsset(selectedCurrency);
      const memo = utf8ToHex(sessionId);

      const transactionData: EvmTransactionData = {
        abi,
        address: selectedCurrency.contractAddress as `0x${string}`,
        functionName: 'transfer',
        args: [depositAddress, transferAmount],
        dataSuffix: memo,
      };

      store.setValue((state) => {
        state.anyCryptoDeposit.selectedNetwork =
          selectedCurrency.currencyNetwork as OpenapiPostV1CryptoInternalRelayQuoteInputInputChainEnum;
        state.overlay = 'CONFIRM_PAYMENT';
      });

      await walletClient.evmSendTransaction(transactionData);

      store.setValue((state) => {
        state.overlay = null;
        state.screen = 'ANY_CRYPTO_PROGRESS';
      });
    } catch (error) {
      store.setValue((state) => {
        state.overlay = null;
      });
      await resyncSessionState();
      handleError({error, intl, context: 'transfer'});
    } finally {
      setIsProcessing(false);
    }
  };

  return handleRelayPayment;
};
